import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { formatDate } from '@angular/common';
import { DeviceClient, DeviceDiagnosticMeasurement, DeviceDiagnosticViewModel } from '../../../services/nswag/nswagclient';
import { IOpsViewerMessagesService } from '../../../services/services';

interface DeviceDiagnosticRow {
    deviceId: string;
    deviceName: string;
    measurementName: string;
    utcTimestamp?: Date;
    timestampDisplay: string;
    valueDisplay: string;
    tagsSummary: string;
}

@Component({
    selector: 'app-device-diagnostics',
    templateUrl: './device-diagnostics.component.html',
    styleUrls: ['./device-diagnostics.component.css'],
    standalone: false
})
export class DeviceDiagnosticsComponent implements OnInit {
    rows: DeviceDiagnosticRow[] = [];
    loading = false;
    lastUpdated?: Date;

    constructor(
        private readonly deviceClient: DeviceClient,
        private readonly messageService: IOpsViewerMessagesService,
        private readonly titleService: Title,
        @Inject(LOCALE_ID) private readonly locale: string) { }

    ngOnInit(): void {
        this.titleService.setTitle('OpsViewer Device Diagnostics');
        this.loadDiagnostics();
    }

    refresh(): void {
        this.loadDiagnostics();
    }

    private loadDiagnostics(): void {
        this.loading = true;
        this.deviceClient.getLatestDeviceDiagnostics(undefined, undefined).subscribe({
            next: response => {
                this.rows = this.flattenDiagnostics(response ?? []);
                this.lastUpdated = new Date();
                this.loading = false;
            },
            error: error => {
                this.loading = false;
                this.rows = [];
                this.messageService.errorMessage(error, 'Could not load device diagnostics');
            }
        });
    }

    private flattenDiagnostics(devices: DeviceDiagnosticViewModel[]): DeviceDiagnosticRow[] {
        const rows: DeviceDiagnosticRow[] = [];

        devices.forEach(device => {
            const measurements = device.measurements ?? [];
            if (measurements.length === 0) {
                rows.push({
                    deviceId: device.deviceId ?? '',
                    deviceName: device.deviceName ?? '',
                    measurementName: 'No recent data',
                    utcTimestamp: undefined,
                    timestampDisplay: '—',
                    valueDisplay: '',
                    tagsSummary: ''
                });
                return;
            }

            measurements.forEach(measurement => {
                rows.push(this.createRow(device, measurement));
            });
        });

        return rows.sort((left, right) => {
            const leftTime = left.utcTimestamp ? left.utcTimestamp.getTime() : 0;
            const rightTime = right.utcTimestamp ? right.utcTimestamp.getTime() : 0;
            return rightTime - leftTime;
        });
    }

    private createRow(device: DeviceDiagnosticViewModel, measurement: DeviceDiagnosticMeasurement): DeviceDiagnosticRow {
        const tags = measurement.tags ?? {};

        const tagsSummary = Object.entries(tags)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');

        return {
            deviceId: device.deviceId ?? '',
            deviceName: device.deviceName ?? '',
            measurementName: measurement.measurementName ?? '',
            utcTimestamp: measurement.utcTimestamp,
            timestampDisplay: measurement.utcTimestamp
                ? formatDate(measurement.utcTimestamp, 'M/d/yy, h:mm:ss a', this.locale)
                : '—',
            valueDisplay: this.formatValue(measurement.value),
            tagsSummary
        };
    }

    private formatValue(rawValue: any): string {
        if (rawValue === null || rawValue === undefined) {
            return '';
        }

        if (typeof rawValue === 'number') {
            return rawValue.toLocaleString(undefined, { maximumFractionDigits: 4 });
        }

        if (typeof rawValue === 'object') {
            try {
                return JSON.stringify(rawValue);
            } catch (_error) {
                return String(rawValue);
            }
        }

        return String(rawValue);
    }
}
